import React, { useEffect, useState } from 'react';
import { Button, Grid, Typography, Card, CardContent, TablePagination, Chip, Checkbox, Toolbar } from '@mui/material';
import Config from '../../config';
import SearchBox from '../../components/searchbox';
import Helper from '../../helpers';
import ExtendedSelectBase from '../../components/select';
import { Categories } from '../settings/account_info';
import { Link } from 'react-router-dom';
import Image from '../../components/image';
import myprofile from '../../images/my_profile.svg';
import FilterOdata from '../../components/filter';
import axios from 'axios';
import NoImageIcon from '@mui/icons-material/Image';
import { ContactDialog } from './profile';
import {Star as FeaturedIcon} from '@mui/icons-material';
import shipment_time from '../../images/shipment_time.png';
import top_seller from '../../images/top_seller.png';

// ExtendedSelect is a JS component; its inferred type only exposes RefAttributes,
// so cast to ComponentType<any> to allow the variant/options/onChange/style props.
const ExtendedSelect = ExtendedSelectBase as React.ComponentType<any>;

const performanceOptions = [{label: "On-Time Shipment", value: "shipmenttime"}, {label: "Top Seller", value: "topsellers"}];

export interface NetworkCompanyRow {
    id: string | number;
    companyName?: string;
    categories?: string[];
    merchFirstName?: string;
    merchLastName?: string;
    merchEmail?: string;
    featured?: boolean;
    shipmentTimeScore?: number;
    topSeller?: boolean;
    [key: string]: unknown;
}

export interface NetworkCompaniesProps {
    // Injected by <Route render={(props) => ...}/> in App.js.
    history?: any;
    location?: any;
    setTitle?: (title: string) => void;
    isRetailer?: boolean | number;
}

interface NetworkCompaniesState {
    term: string;
    termValue: string;
    categories: string[];
    performance: string;
    companies: NetworkCompanyRow[];
    pageSize: number;
    page: number;
    total: number;
    selected: (string | number)[];
    showMessage: boolean;
    loaded?: boolean;
    disableSend?: boolean;
    messageSent?: boolean | string;
    message?: string;
    messageEmpty?: boolean;
}

// The class read `this.state` synchronously right after `await this.setState(...)`
// inside loadData's call sites. In hooks, state updates are async, so the fresh
// values are threaded into loadData explicitly via `overrides` to preserve the
// exact request each caller made.
type LoadOverrides = Partial<Pick<NetworkCompaniesState, 'term' | 'categories' | 'performance' | 'page' | 'pageSize'>>;

const NetworkCompanies = (props: NetworkCompaniesProps) => {
    const [term, setTerm] = useState<NetworkCompaniesState['term']>('');
    const [termValue, setTermValue] = useState<NetworkCompaniesState['termValue']>('');
    const [categories, setCategories] = useState<NetworkCompaniesState['categories']>([]);
    const [performance, setPerformance] = useState<NetworkCompaniesState['performance']>('');
    const [companies, setCompanies] = useState<NetworkCompaniesState['companies']>([]);
    const [pageSize] = useState<NetworkCompaniesState['pageSize']>(24);
    const [page, setPage] = useState<NetworkCompaniesState['page']>(0);
    const [total, setTotal] = useState<NetworkCompaniesState['total']>(0);
    const [selected, setSelected] = useState<NetworkCompaniesState['selected']>([]);
    const [showMessage, setShowMessage] = useState<NetworkCompaniesState['showMessage']>(false);
    const [loaded, setLoaded] = useState<NetworkCompaniesState['loaded']>(undefined);
    const [disableSend, setDisableSend] = useState<NetworkCompaniesState['disableSend']>(undefined);
    const [messageSent, setMessageSent] = useState<NetworkCompaniesState['messageSent']>(undefined);
    const [message, setMessage] = useState<NetworkCompaniesState['message']>(undefined);
    const [messageEmpty, setMessageEmpty] = useState<NetworkCompaniesState['messageEmpty']>(undefined);

    const setCategoriesInUrl = (cats: string[]) => {
        let catString = cats.join(";");
        new Helper().setUrlObject("c", catString, props.history, props.location);
    };

    const getCategoriesFromUrl = () => {
        let term = new Helper().queryString()['c'];
        return term ? term.split(";") : null;
    };

    const getCategoryFilter = (cats: string[], perf: string) => {
        let categoryFilter = null;
        if(cats && cats.length > 0){
            categoryFilter = {filters: (cats || []).map(r => ({operator: 'anyeq', field: 'categories', value: r})), logic: 'or'}
        }
        let perfFilter = null;
        if(perf === "shipmenttime"){
            perfFilter = {operator: 'ge', field: 'shipmentTimeScore', value: 90};
        } else if(perf === "topsellers"){
            perfFilter = {operator: 'eq', field: 'topSeller', value: true};
        }
        let active = [categoryFilter, perfFilter].filter(r => !(!r));
        let filter = active.length > 0 ? {filters: active, logic: 'and'} : null;
        return filter;
    };

    const loadData = async (overrides: LoadOverrides = {}) => {
        const curTerm = overrides.term ?? term;
        const curCategories = overrides.categories ?? categories;
        const curPerformance = overrides.performance !== undefined ? overrides.performance : performance;
        const curPage = overrides.page ?? page;
        const curPageSize = overrides.pageSize ?? pageSize;
        let url = Config.api + "/odata/Company/Functions.NetworkSearch?select=id,companyName,categories,merchFirstName,merchLastName,merchEmail,featured,shipmentTimeScore,topSeller&$count=true&$orderby=featured desc,companyName asc";
        url += "&$top=" + curPageSize;
        url += "&$skip=" + (curPage * curPageSize);
        let filter = getCategoryFilter(curCategories, curPerformance);
        if(filter){
            url += "&$filter=" + new FilterOdata().ToOData(filter);
        }
        if(curTerm){
            url += "&term=" + curTerm;
        }
        let data = (await axios.get(url)).data;
        setCompanies(data.value);
        setTotal(data["@odata.count"]);
        setSelected([]);
    };

    const onEnter = async (e: React.SyntheticEvent<HTMLInputElement>) => {
        var newTerm = e.currentTarget.value;
        setTerm(newTerm);
        setPage(0);
        new Helper().setTermInUrl(newTerm, props.history, props.location);
        loadData({term: newTerm, page: 0});
    };

    const updateCategories = async (e: string[]) => {
        setCategoriesInUrl(e);
        setCategories(e);
        setPage(0);
        loadData({categories: e, page: 0});
    };

    const updatePerformance = async (e: string) => {
        new Helper().setUrlObject("perf", e, props.history, props.location);
        setPerformance(e);
        setPage(0);
        loadData({performance: e, page: 0});
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
        setPage(newPage);
        loadData({page: newPage});
    };

    const select = (id: string | number) => {
        setSelected(prev => prev.indexOf(id) > -1 ? prev.filter(r => r !== id) : [...prev, id]);
    };

    const bulkConnect = () => {
        setDisableSend(false);
        setMessageSent(false);
        setShowMessage(true);
        setMessage('');
        setMessageEmpty(true);
    };

    const createThreads = async () => {
        setDisableSend(true);
        let selectedIds = [...selected];
        let sent = 0;
        for(let i = 0; i < selectedIds.length; i++){
            try{
                await axios.post(Config.api + '/odata/Company/Functions.NewThread', {
                    Body: message,
                    RequireAck: false,
                    Recipients: [selectedIds[i]]
                });
                sent++;
            }catch(e){

            }
        }
        setMessageSent(`${sent} messages sent! You can continue the conversation in the message center.`);
        setDisableSend(false);
    };

    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Discover Partners');
        }
        let urlTerm = new Helper().getTermFromUrl();
        let cats = getCategoriesFromUrl() || [];
        let perf = new Helper().queryString()['perf'];
        setTermValue(urlTerm);
        setTerm(urlTerm);
        setLoaded(true);
        setCategories(cats);
        setPerformance(perf);
        loadData({term: urlTerm, categories: cats, performance: perf});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if(!loaded){
        return <div/>;
    }
    var { isRetailer } = props;
    var retailerSupplier = isRetailer ? "Supplier" : "Retailer";
    return (
        <Grid container spacing={2}>
            <Grid container size={12}>
                <Card style={{flex: 1}}>
                    <Toolbar className='lbtoolbar'>Logicbroker's Connected Commerce Network®</Toolbar>
                    <CardContent style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                        <Typography style={{textAlign: 'left', marginBottom: '1em'}}>Easily connect with thousands of qualified suppliers to expand your assortment to offer the products your customers are looking for.</Typography>
                        <div style={{display: 'flex', gap: '1em', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'end'}}>
                            <div style={{flex: 1, textAlign: 'left', display: 'flex', alignItems: 'center'}}>
                                {selected.length > 0 && <div>
                                        <Typography style={{fontWeight: 'bold', display: 'inline-block', marginRight: '1em'}}>{selected.length} Selected</Typography>
                                        <Button variant='outlined' onClick={bulkConnect}>Connect</Button>
                                </div>}
                            </div>
                            <div style={{display: 'inline-block'}}>
                                <SearchBox inputStyle={{minWidth: '250px'}} placeholder={`Search ${retailerSupplier.toLowerCase()}s`} onEnter={onEnter} handleChange={(e) => setTermValue(e.currentTarget.value)} term={termValue}/>
                            </div>
                            <ExtendedSelect style={{flex: 1, minWidth: '250px'}} variant='outlined' placeholder='Filter by performance...' value={performance} options={performanceOptions} onChange={(e) => updatePerformance(e)} isClearable/>
                            <ExtendedSelect style={{flex: 1, minWidth: '500px'}} variant='outlined' placeholder='Filter categories...' value={categories} options={Categories} onChange={(e) => updateCategories(e)}/>
                            <Button component={Link} variant='contained' color='primary' to='/profile/account-information'><img src={myprofile} alt='' style={{marginRight: '0.5em', height: '1.5em'}}/> View my profile</Button>
                        </div>
                    </CardContent>
                </Card>
            </Grid>
            {companies.map(r => <Grid size={{ md: 3 }} key={r.id} style={{position: 'relative', overflow: 'hidden', padding: '5px'}}>
                    <Card style={{textAlign: 'left', height: '15em', overflow: 'hidden', display: 'flex'}}>
                        <Checkbox style={{position: 'absolute', top: '5px', left: '5px'}} checked={selected.indexOf(r.id) > -1} onChange={() => select(r.id)}/>
                        <CardContent style={{paddingLeft: '30px', display: 'flex', flex: 1, flexDirection: 'column'}}>
                            <div style={{height: '50px'}}>
                                <div style={{height: '100%', display: 'table'}}>
                                    <div style={{verticalAlign: 'middle', display: 'table-cell'}}>
                                        <Image style={{height: 'auto', maxHeight: '50px', mixBlendMode: 'multiply', verticalAlign: 'middle', maxWidth: '12em'}} src={`${Config.logos}${r.id}large.jpg`} altImage={<NoImageIcon style={{color: '#ccc', height: '50px', width: '50px'}}/>} />
                                    </div>
                                </div>
                                <div style={{position: 'absolute', top: '21px', right: '29px', display: 'flex', alignItems: 'center'}}>
                                    <Button variant='contained' color='primary' component={Link} to={`/connected-commerce-network/profile?id=${r.id}`}>View</Button>
                                </div>
                            </div>
                            <div><Typography variant='h6'>{r.companyName}</Typography></div>
                            {!(!r.merchEmail) && <Typography color='textSecondary' style={{fontSize: '13px'}}><span style={{fontWeight: 'bold'}}>Contact:</span> {r.merchFirstName} {r.merchLastName} <a href={`mailto:${r.merchEmail}`}>{r.merchEmail}</a></Typography>}
                            <div style={{display: 'flex', gap: '0.5em', flexWrap: 'wrap', flex: 1, overflow: 'hidden', alignContent: 'flex-start'}}>
                                {(r.categories || []).map(c => <Chip variant='outlined' size='small' key={c} label={c}/>)}
                            </div>
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                {r.topSeller && <img style={{maxHeight: '1.5em', maxWidth: '1.5em', marginRight: '0.25rem'}} src={top_seller} alt='' title='Top Seller'/>}
                                {r.shipmentTimeScore >= 90 && <img style={{maxHeight: '1.5em', maxWidth: '1.5em', marginRight: '0.25rem'}} src={shipment_time} alt='' title='On-Time Shipments'/>}
                                {r.featured ? <span><FeaturedIcon style={{color: 'gold', verticalAlign: 'middle'}}/><span style={{verticalAlign: 'middle', marginRight: '1em'}}>Featured</span></span> : ''}
                            </div>
                        </CardContent>
                    </Card>
                </Grid>)}
                <Grid size={{ md: 12 }}>
                <TablePagination
                component="div"
                count={total}
                rowsPerPage={pageSize}
                page={page}
                backIconButtonProps={{
                    'aria-label': 'Previous Page',
                }}
                rowsPerPageOptions={[]}
                nextIconButtonProps={{
                    'aria-label': 'Next Page',
                }}
                labelDisplayedRows={() => ''}
                onPageChange={handleChangePage}
                />
            </Grid>
            <ContactDialog
                open={showMessage}
                onClose={() => setShowMessage(false)}
                disabled={disableSend}
                companyName={`the ${selected.length} companies you have selected`}
                merchWarning={`Merchandising contacts may not be Logicbroker users and may not receive your message. Reach out directly through email to ensure they see your message.`}
                message={message}
                messageEmpty={messageEmpty}
                onMessageChange={(html, isEmpty) => { setMessage(html); setMessageEmpty(isEmpty); }}
                submit={createThreads}
                sentMessage={messageSent}
            />
            </Grid>
    );
};

export default NetworkCompanies;
